/**
 * The frame every screen of this template renders into: the header, the payment timer right under
 * it, the screen body, the action buttons and the order number at the very bottom.
 */
import React, { useEffect, type ReactNode } from 'react';
import {
  GateRoot,
  GateCard,
  GateHeader,
  GateTimer,
  GateOrderNumber,
  GateButtonStack,
  GatePrimaryButton,
  GateQuietButton,
} from './ui';

// ─── GateScreen — единая обёртка для всех экранов ───────────────────────────
export interface GateAction {
  label: ReactNode;
  onClick: () => void;
  variant?: 'green' | 'grey';
  confirmLeave?: boolean;
  /** CPAYMENT-1786: stable click identity for telemetry — see the note on GateScreen. */
  dataSentryElement?: string;
}

interface GateScreenProps {
  uuid?: string;
  timeLeft?: string;   // если передан — под шапкой показывается таймер
  actions?: GateAction[];
  footerSlot?: ReactNode; // для нестандартных раскладок кнопок
  children: ReactNode;
}

/**
 * CPAYMENT-1786: `data-sentry-element` is read by the Sentry SDK itself — `htmlTreeAsString` returns
 * its value **instead of** the `tag#id.class` chain, so a click lands in spans, breadcrumbs and
 * replays under a readable name rather than as `div.sc-kMriOJ > button.sc-jBeCGk`. Those generated
 * class names change with every build, which is why click attribution used to be guesswork.
 *
 * The names are part of the analysis contract (see the mechanics doc): renaming one silently breaks
 * comparison with everything recorded before.
 */
export const GateScreen = ({ uuid, timeLeft, actions, footerSlot, children }: GateScreenProps) => {
  const hasConfirmLeave = actions?.some(a => a.confirmLeave) ?? false;

  useEffect(() => {
    if (!hasConfirmLeave) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      (e as any).returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasConfirmLeave]);

  return (
    <GateRoot>
      <GateCard>
        <GateHeader />
        {timeLeft && <GateTimer timeLeft={timeLeft} />}
        {children}
        {footerSlot}
        {/* Единственное действие экрана — главное, как бы его ни пометил экран: одинокая текстовая
            кнопка выглядит как отсутствие кнопки. */}
        {actions && actions.length > 0 && (
          <GateButtonStack>
            {actions.map((a, i) =>
              a.variant === 'green' || actions.length === 1
                ? <GatePrimaryButton key={i} data-sentry-element={a.dataSentryElement} onClick={a.onClick}>{a.label}</GatePrimaryButton>
                : <GateQuietButton key={i} data-sentry-element={a.dataSentryElement} onClick={a.onClick}>{a.label}</GateQuietButton>,
            )}
          </GateButtonStack>
        )}
        {uuid && <GateOrderNumber uuid={uuid} />}
      </GateCard>
    </GateRoot>
  );
};
